import { useState } from 'react'
import type { AccessLevel } from '@kronos/domain'
import { repository } from '../../data'
import { Modal } from '../kit'
import type { View } from './derive'
import { endOfDay } from './format'
import { useWrite } from './parts'

const LENGTHS = [
  { id: 'always', label: 'Until removed', days: 0 },
  { id: '1', label: '1 day', days: 1 },
  { id: '3', label: '3 days', days: 3 },
  { id: '7', label: '1 week', days: 7 },
  { id: '30', label: '30 days', days: 30 },
  { id: 'date', label: 'Until a date', days: 0 },
] as const
type Length = (typeof LENGTHS)[number]['id']

/**
 * Give someone — a person or a team — access to something — a machine or a
 * group — until removed or for a while. One form for every rule: a small team
 * gives a person a machine, a large one a team a group; Omar's two days on
 * R-147 is the same thing with an end.
 */
export function GiveAccess({
  view,
  preset = {},
  onClose,
}: {
  view: View
  preset?: { userId?: string; machineId?: string; length?: Length }
  onClose: () => void
}): JSX.Element {
  const [whoKind, setWhoKind] = useState<'person' | 'team'>('person')
  const [who, setWho] = useState(preset.userId ?? '')
  const [whatKind, setWhatKind] = useState<'machine' | 'group'>('machine')
  const [what, setWhat] = useState(preset.machineId ?? '')
  const [length, setLength] = useState<Length>(preset.length ?? 'always')
  const [date, setDate] = useState('')
  const [level, setLevel] = useState<AccessLevel>('full')
  const [note, setNote] = useState('')

  const chooseWho = (k: 'person' | 'team'): void => {
    setWhoKind(k)
    setWho('')
  }
  const chooseWhat = (k: 'machine' | 'group'): void => {
    setWhatKind(k)
    setWhat('')
  }
  const chosen = LENGTHS.find((l) => l.id === length) ?? LENGTHS[0]
  const expiresAt = length === 'always' ? null : length === 'date' ? (date ? endOfDay(date) : '') : new Date(Date.now() + chosen.days * 86_400_000).toISOString()
  const ready = who !== '' && what !== '' && expiresAt !== ''
  const whoName = whoKind === 'person' ? view.person(who)?.name : view.team(who)?.name
  const whatName = whatKind === 'machine' ? view.machine(what)?.name : view.group(what)?.name

  const give = useWrite(
    () =>
      repository
        .setRule({
          userId: whoKind === 'person' ? who : null,
          teamId: whoKind === 'team' ? who : null,
          machineId: whatKind === 'machine' ? what : null,
          groupId: whatKind === 'group' ? what : null,
          level,
          expiresAt: expiresAt || null,
          note,
        })
        .then((r) => (onClose(), r)),
    () => `${whoName} can SSH into ${whatName}${expiresAt ? ` until ${new Date(expiresAt).toLocaleDateString(undefined, { day: 'numeric', month: 'short' })}` : ''}`,
  )

  const pick = <T extends string>(value: T, set: (v: T) => void, options: readonly (readonly [T, string])[], label: string) => (
    <div className="segmented" role="tablist" aria-label={label}>
      {options.map(([id, text]) => (
        <button key={id} role="tab" aria-selected={value === id} onClick={() => set(id)}>{text}</button>
      ))}
    </div>
  )

  return (
    <Modal
      title="Give access"
      subtitle="A person or a team, onto a machine or a group — until removed, or for a while."
      onClose={onClose}
      footer={<button className="primary" disabled={!ready || give.pending} onClick={() => give.run()}>Give access</button>}
    >
      <div className="stack">
        <div className="stack" style={{ gap: 4 }}>
          <span className="field-label">Who</span>
          {pick(whoKind, chooseWho, [['person', 'A person'], ['team', 'A team']], 'Who')}
          <select value={who} onChange={(e) => setWho(e.currentTarget.value)}>
            <option value="">{whoKind === 'person' ? 'Pick a person…' : 'Pick a team…'}</option>
            {(whoKind === 'person' ? view.people : view.teams).map((x) => (
              <option key={x.id} value={x.id}>{x.name}</option>
            ))}
          </select>
        </div>
        <div className="stack" style={{ gap: 4 }}>
          <span className="field-label">Into</span>
          {pick(whatKind, chooseWhat, [['machine', 'A machine'], ['group', 'A group']], 'Into')}
          <select value={what} onChange={(e) => setWhat(e.currentTarget.value)} className={whatKind === 'machine' ? 'cx-mono' : ''}>
            <option value="">{whatKind === 'machine' ? 'Pick a machine…' : 'Pick a group…'}</option>
            {whatKind === 'machine'
              ? view.machines.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)
              : view.groups.map((g) => <option key={g.id} value={g.id}>{g.name} · {g.machineIds.length}</option>)}
          </select>
        </div>
        <div className="stack" style={{ gap: 4 }}>
          <span className="field-label">For how long</span>
          <div className="cx-lengths">
            {LENGTHS.map((l) => (
              <button key={l.id} aria-pressed={length === l.id} className="cx-length" onClick={() => setLength(l.id)}>{l.label}</button>
            ))}
          </div>
          {length === 'date' ? <input type="date" value={date} onChange={(e) => setDate(e.currentTarget.value)} /> : null}
        </div>
        <div className="stack" style={{ gap: 4 }}>
          <span className="field-label">Level</span>
          {pick(level, setLevel, [['full', 'Full'], ['restricted', 'Restricted']], 'Level')}
        </div>
        <label className="stack" style={{ gap: 4 }}>
          <span className="field-label">Why</span>
          <input value={note} placeholder="optional — e.g. swapping the left wrist actuator" onChange={(e) => setNote(e.currentTarget.value)} />
        </label>
      </div>
    </Modal>
  )
}
